import React, { useState, useCallback } from 'react';
import {
  Box,
  Button,
  Avatar,
  Paper,
  Stack,
  Alert,
} from '@mui/material';
import {
  Send as SendIcon,
} from '@mui/icons-material';
import { User } from '../../types';
import MentionInput from '../common/MentionInput';
import { SelectedMention } from '../../types/mention';
import { mentionParser } from '../../utils/mention-parser';

interface CommentFormProps {
  personId: string;
  currentUser: User;
  parentId?: string;
  onSubmit: (data: any) => Promise<any>;
  onCancel?: () => void;
  placeholder?: string;
  autoFocus?: boolean;
}

const CommentForm: React.FC<CommentFormProps> = ({
  personId,
  currentUser,
  parentId,
  onSubmit,
  onCancel,
  placeholder = 'Write a comment...',
  autoFocus = false,
}) => {
  const [content, setContent] = useState('');
  const [selectedMentions, setSelectedMentions] = useState<SelectedMention[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(async () => {
    if (!content.trim()) {
      setError('Please enter a comment');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const commentData: any = {
        content: content.trim(),
        personId,
        mentions: mentionParser.parse(content, selectedMentions),
      };

      // Only add parentId if it exists
      if (parentId) {
        commentData.parentId = parentId;
      }

      await onSubmit(commentData);

      // Clear form on success
      setContent('');
      setSelectedMentions([]);

      // Call cancel handler if this is a reply form
      if (onCancel) {
        onCancel();
      }
    } catch (err) {
      console.error('Error submitting comment:', err);
      setError(err instanceof Error ? err.message : 'Failed to post comment');
    } finally {
      setIsSubmitting(false);
    }
  }, [content, selectedMentions, personId, parentId, onSubmit, onCancel]);

  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent) => {
      // Submit on Cmd/Ctrl + Enter
      if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
        event.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        {/* Error message */}
        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Form */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          {/* Avatar */}
          <Avatar sx={{ width: 40, height: 40 }}>
            {currentUser.displayName?.charAt(0) || '?'}
          </Avatar>

          {/* Text input */}
          <Box sx={{ flexGrow: 1 }}>
            <MentionInput
              value={content}
              onChange={(newValue, newMentions) => {
                setContent(newValue);
                setSelectedMentions(newMentions);
              }}
              onKeyPress={handleKeyPress}
              contentType="comment"
              targetId={personId}
              placeholder={placeholder}
              rows={parentId ? 2 : 3}
              fullWidth
              disabled={isSubmitting}
              autoFocus={autoFocus}
              helperText="Tip: Use Cmd/Ctrl + Enter to submit, type @ to mention someone"
            />
          </Box>
        </Box>

        {/* Actions */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          {onCancel && (
            <Button
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!content.trim() || isSubmitting}
            startIcon={<SendIcon />}
          >
            {isSubmitting ? 'Posting...' : parentId ? 'Reply' : 'Post Comment'}
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
};

export default CommentForm;
